import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDraftSync } from '@/hooks/useDraftSync';
import { useMediaIndexSync } from '@/hooks/useMediaIndexSync';
import { useArticleDrafts } from '@/hooks/useArticleDrafts';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { buildMediaIndex } from '@/lib/mediaSync';

/** How often sync-down may run (login, window refocus). */
const DOWN_THROTTLE_MS = 5 * 60 * 1000;
/** Quiet period after a local change before syncing up. */
const UP_DEBOUNCE_MS = 3000;

function lastDownKey(pubkey: string): string {
  return `wikistar:auto-sync:down:${pubkey}`;
}

/**
 * Automatic two-way sync for article drafts and the media library index
 * — the always-on counterpart to the manual Sync up/down buttons on
 * `/drafts` and `/media`.
 *
 *  - **Down**: on login and whenever the window regains focus (throttled
 *    to once per 5 minutes per account), pulls encrypted drafts (NIP-59
 *    gift wraps) and the encrypted media index, merging newest-wins.
 *  - **Up**: watches the local stores and, after 3s of quiet, pushes
 *    what changed — only drafts newer than their last known sync (per-id
 *    stamps, so a pulled draft is never wrapped and published straight
 *    back), and the media index only when its fingerprint differs from
 *    the last push. For the index, push-after-merge is deliberate: it's
 *    one replaceable event, so pushing the union is how devices converge.
 *
 * Auto-up is gated on the first sync-down attempt completing for the
 * account: pulling first means a fresh device never pushes stale state
 * over newer remote copies, and pulled items can't bounce straight back
 * up. Everything is silent (console logs inside the sync hooks only) —
 * background sync must never toast at the user.
 */
export function AutoSync() {
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;
  const draftSync = useDraftSync();
  const mediaSync = useMediaIndexSync();
  const { drafts } = useArticleDrafts();
  const { items } = useMediaLibrary();

  const canSync = Boolean(pubkey) && draftSync.canSync && mediaSync.canSync;

  // The sync hooks return fresh object literals every render, which would
  // churn every effect below if used as deps — keep latest-in-ref instead.
  const draftSyncRef = useRef(draftSync);
  draftSyncRef.current = draftSync;
  const mediaSyncRef = useRef(mediaSync);
  mediaSyncRef.current = mediaSync;

  // Draft id → updatedAt already pushed (or pulled) this session.
  const draftStampsRef = useRef(new Map<string, number>());
  // Fingerprint of the media index as last pushed (or merged from remote).
  const mediaFpRef = useRef('');
  // Latest store snapshots for async code (post-await reads).
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Auto-up gates on the first sync-down attempt for THIS account.
  const [downDoneFor, setDownDoneFor] = useState<string>();
  const downRunningRef = useRef(false);

  const runDown = useCallback(async () => {
    if (!pubkey || downRunningRef.current) return;
    const last = Number(localStorage.getItem(lastDownKey(pubkey)) ?? 0);
    if (Date.now() - last < DOWN_THROTTLE_MS) {
      // Throttled out — but a recent pull for THIS account by definition
      // already happened, so auto-up may proceed.
      setDownDoneFor(pubkey);
      return;
    }
    // Stamp before awaiting so concurrent triggers (focus + mount) don't
    // double-pull; a failed attempt simply retries after the throttle.
    localStorage.setItem(lastDownKey(pubkey), String(Date.now()));
    downRunningRef.current = true;

    const beforeDrafts = new Map(draftsRef.current.map((d) => [d.id, d.updatedAt]));
    try {
      await draftSyncRef.current.syncDown();
    } catch {
      // Logged inside the sync hooks; background sync stays silent.
    }
    try {
      await mediaSyncRef.current.syncDown();
    } catch {
      // Same.
    }

    // Mark drafts the pull created or updated locally as already-synced —
    // otherwise the up-watcher would wrap and publish them right back.
    const stamps = draftStampsRef.current;
    for (const draft of draftsRef.current) {
      const before = beforeDrafts.get(draft.id);
      if (before === undefined || draft.updatedAt > before) {
        stamps.set(draft.id, draft.updatedAt);
      }
    }
    // The post-merge index IS the remote state as far as we know — treat
    // its fingerprint as synced so an unchanged library doesn't re-push.
    mediaFpRef.current = JSON.stringify(buildMediaIndex(itemsRef.current ?? []).entries);

    downRunningRef.current = false;
    setDownDoneFor(pubkey);
  }, [pubkey]);

  // Initial sync-down on login / account switch.
  useEffect(() => {
    if (!canSync || !pubkey) return;
    void runDown();
  }, [canSync, pubkey, runDown]);

  // Re-pull when the window regains focus (throttled inside runDown).
  useEffect(() => {
    if (!canSync) return;
    const onFocus = () => void runDown();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void runDown();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [canSync, runDown]);

  // Sync up: drafts newer than their last known sync, debounced.
  useEffect(() => {
    if (!canSync || downDoneFor !== pubkey) return;

    const pending = drafts.filter((draft) => {
      const synced =
        draftStampsRef.current.get(draft.id) ?? draftSyncRef.current.getSyncRecord(draft.id)?.at ?? 0;
      return draft.updatedAt > synced;
    });
    if (pending.length === 0) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        for (const draft of pending) {
          if (cancelled) return;
          try {
            await draftSyncRef.current.syncUp(draft);
            draftStampsRef.current.set(draft.id, draft.updatedAt);
          } catch {
            // Leave unmarked — retried on the next change or refocus.
          }
        }
      })();
    }, UP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canSync, downDoneFor, pubkey, drafts]);

  // Sync up: media index whose fingerprint changed, debounced.
  useEffect(() => {
    if (!canSync || downDoneFor !== pubkey || items === undefined) return;

    const fingerprint = JSON.stringify(buildMediaIndex(items).entries);
    if (fingerprint === mediaFpRef.current) return;

    const timer = setTimeout(() => {
      void (async () => {
        try {
          await mediaSyncRef.current.syncUp();
          mediaFpRef.current = fingerprint;
        } catch {
          // Fingerprint ref stays stale — retried on the next change.
        }
      })();
    }, UP_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [canSync, downDoneFor, pubkey, items]);

  return null;
}
